import { caretDownIcon, caretRightIcon } from "../../elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import button from "@foretagsplatsen/finsit-design-system/widget-js/elements/button";

/**
 * `TreeItemWidget` is a `treeWidget` node. It computes its children lazily
 * for speed concerns, except for commands walking down the tree
 * (`findBy` by example).
 *
 * @param {TreeItemWidget} [spec.parent] - Parent of the current node, or `null` if the node is a root
 * @param {*} spec.item - Domain object model of the node
 * @param {integer} spec.depth - Node depth
 * @param {boolean} spec.isExpanded - True is the node is expanded, false if collapsed
 * @param {function} spec.getLabel - Function which given a model returns the its node label
 * @param {function} spec.getId - Function which given a model returns the its node id
 * @param {function} spec.getChildren - Function which given a model returns the its children
 * @param {function} [spec.canBeSelected] - Function which given a model returns true if the its node can be selected
 * @param {function} spec.searchFn - Function taking a model and a search-string and
 * returns `true` if the model matches the search-string
 * @param {integer} spec.autoOpen - Expand the receiver if value is greater than depth
 * @param {string} spec.searchString - String used to search through the tree nodes
 */

/**
 * TODO: write documentation
 */
export default class TreeItemWidget extends Widget {
	constructor(spec = {}) {
		super(...arguments);

		this._parent = spec.parent;
		this._previous = null;
		this._next = null;

		this._item = spec.item;
		this._depth = spec.depth;
		this._isExpanded = spec.isExpanded;

		this._getLabel = spec.getLabel;
		this._getId = spec.getId;
		this._getChildren = spec.getChildren;
		this._canBeSelected = spec.canBeSelected || (() => true);
		this._searchFn = spec.searchFn;
		this._autoOpen = spec.autoOpen;

		this._isSelected = false;
		this._searchString = spec.searchString;

		this._childItems = null;

		if (this._autoOpen !== undefined && this._autoOpen > this._depth) {
			this._isExpanded = true;
		}

		this.onSelectionChange = this._events.createEvent();
	}

	getModel() {
		return this._item;
	}

	getParent() {
		return this._parent;
	}

	setPrevious(node) {
		this._previous = node;
	}

	setNext(node) {
		this._next = node;
	}

	filterNodes(searchString) {
		this._searchString = searchString;
		if (this._childItems) {
			this._childItems.forEach((child) =>
				child.filterNodes(searchString),
			);
		}

		this.update();
	}

	forceRefresh() {
		this._childItems = null;
		this.update();
	}

	collapseAll() {
		this._childItemsDo((child) =>
			child.withNoUpdate(() => child.collapseAll()),
		);

		this._collapse();
	}

	expandAll() {
		this._resolveChildren();

		this._childItemsDo((child) =>
			child.withNoUpdate(() => child.expandAll()),
		);

		this._expand();
	}

	findNode(item, fn) {
		if (this._getId(item) === this._getId(this._item)) {
			if (fn) {
				fn(this);
			}
			return true;
		}

		this._resolveChildren();
		const match = this._childItems.some((child) =>
			child.findNode(item, fn),
		);

		if (match) {
			this._expand();
		}

		return match;
	}

	select() {
		if (this._isSelected || !this._canBeSelected(this._item)) {
			return;
		}

		this._isSelected = true;
		this.onSelectionChange.trigger(this);
		this.update();
	}

	deselect() {
		this._isSelected = false;
		this.update();
	}

	shouldBeRendered() {
		if (!this._searchString) {
			return true;
		}

		if (this._searchFn(this._item, this._searchString)) {
			return true;
		}

		this._resolveChildren();

		return this._childItems.some((child) => child.shouldBeRendered());
	}

	removeNode(model) {
		this._childItems = this._childItems.filter(
			(child) => this._getId(child.getModel()) !== this._getId(model),
		);
	}

	addChild(item) {
		if (this._childItems) {
			const node = this._createItem(item);
			const last = this._childItems.at(-1);

			if (last) {
				last.setNext(node);
				node.setPrevious(last);
			}

			this._childItems.push(node);
			node.select();
		} else {
			this._expand();

			this.findNode(item, (node) => node.select());
		}
		this.update();
	}

	renderContentOn(html) {
		if (!this.shouldBeRendered()) {
			return;
		}

		if (this._isLeaf()) {
			this._resolveChildren();
			this._renderLeafOn(html);
		} else if (this._searchString || this._isExpanded) {
			this._renderExpandedOn(html);
		} else {
			this._renderCollapsedOn(html);
		}
	}

	goToLastChild() {
		if (this._isLeaf() || !this._isExpanded) {
			this.select();
			return;
		}

		this._childItems.at(-1).goToLastChild();
	}

	goToNext() {
		if (!this._next) {
			if (this._parent) {
				this._parent.goToNext();
			}
			return;
		}
		this._next.select();
	}

	_didAttach() {
		if (!this._isSelected) {
			return;
		}
		const li = this.asJQuery().find("li.tree-item");
		li.on("keydown", this._keydownHandler.bind(this));
		li.focus();
	}

	_renderExpandedOn(html) {
		this._resolveChildren();

		html.li(
			{
				tabindex: this._isSelected ? "0" : "-1",
				klass: ["tree-item", "tree-expanded", this._extraCssClasses()],
				id: `tree-item-${this._getId(this._item)}`,
			},
			html.div(
				{
					klass: "tree-item-header",
					"data-test": "tree-item-header",
					title: this._getLabel(this._item),
				},
				button(
					{
						as: "span",
						onClick: (event) => {
							event.stopPropagation();
							event.preventDefault();
							this._collapse();
						},
					},
					caretDownIcon({
						dataTest: "expander-down",
					}),
				),
				html.span(
					{
						klass: "tree-item-name",
					},
					this._getLabel(this._item),
				),
			),
			html.ul(
				{
					klass: "tree",
				},
				this._childItems,
			),
		).click(this._clickHandler.bind(this));
	}

	_renderCollapsedOn(html) {
		html.li(
			{
				tabindex: this._isSelected ? "0" : "-1",
				klass: ["tree-item", "tree-collapsed", this._extraCssClasses()],
				id: `tree-item-${this._getId(this._item)}`,
			},
			html.div(
				{
					klass: "tree-item-header",
					"data-test": "tree-item-header",
					title: this._getLabel(this._item),
				},
				button(
					{
						as: "div",
						onClick: (event) => {
							event.stopPropagation();
							event.preventDefault();
							this._isExpanded = true;
							this.update();
						},
					},
					caretRightIcon({
						class: "tree-item-expander",
						dataTest: "expander-right",
					}),
				),
				html.span(
					{
						klass: "tree-item-name",
					},
					this._getLabel(this._item),
				),
			),
		).click(this._clickHandler.bind(this));
	}

	_renderLeafOn(html) {
		html.li(
			{
				tabindex: this._isSelected ? "0" : "-1",
				klass: ["tree-item", "tree-leaf", this._extraCssClasses()],
				id: `tree-item-${this._getId(this._item)}`,
			},
			html.div(
				{
					klass: "tree-item-header",
					"data-test": "tree-item-header",
					title: this._getLabel(this._item),
				},
				html.i({
					klass: "tree-item-leaf",
				}),
				html.span(
					{
						klass: "tree-item-name",
					},
					this._getLabel(this._item),
				),
			),
		).click(this._clickHandler.bind(this));
	}

	_resolveChildren() {
		if (this._childItems !== null) {
			return;
		}

		this._childItems = this._getChildren(this._item).map(
			this._createItem.bind(this),
		);

		this._childItems.forEach((child, index) => {
			child.setPrevious(this._childItems[index - 1]);
			child.setNext(this._childItems[index + 1]);
		});
	}

	_childItemsDo(fn) {
		if (!this._childItems) {
			return;
		}
		this._childItems.forEach(fn);
	}

	_extraCssClasses() {
		let result = " ";

		if (this._canBeSelected(this._item)) {
			result += " tree-item-selectable";
		} else {
			result += " tree-item-not-selectable";
		}

		if (this._isSelected) {
			result += " selected";
		}

		result += ` depth-${this._depth}`;

		return result;
	}

	_isLeaf() {
		return !this._getChildren(this._item).length;
	}

	_clickHandler(event) {
		event.stopPropagation();
		event.preventDefault();
		this.select();
	}

	_keydownHandler(event) {
		const code = event.keyCode;

		switch (code) {
			case 37: {
				this._goLeft();
				event.preventDefault();
				break;
			}
			case 38: {
				this._goUp();
				event.preventDefault();
				break;
			}
			case 39: {
				this._goRight();
				event.preventDefault();
				break;
			}
			case 40: {
				this._goDown();
				event.preventDefault();
				break;
			}
			default: {
				break;
			}
		}
	}

	_goLeft() {
		if (this._isLeaf() || !this._isExpanded) {
			this._goToParent();
			return;
		}
		this._collapse();
	}

	_goUp() {
		if (!this._previous) {
			this._goToParent();
			return;
		}
		this._previous.goToLastChild();
	}

	_goRight() {
		if (this._isLeaf() || this._isExpanded) {
			this._goDown();
			return;
		}

		this._expand();
	}

	_goDown() {
		if (!this._isLeaf() && this._isExpanded) {
			this._childItems[0].select();
			return;
		}

		if (this._next) {
			this._next.select();
			return;
		}

		if (this._parent) {
			this._parent.goToNext();
		}
	}

	_goToParent() {
		if (!this._parent) {
			return;
		}

		this._parent.select();
	}

	_expand() {
		this._isExpanded = true;
		this.update();
	}

	_collapse() {
		this._isExpanded = false;
		this.update();
	}

	_createItem(item) {
		const widget = new this.constructor({
			parent: this,
			item,
			depth: this._depth + 1,
			isExpanded: false,
			getLabel: this._getLabel,
			getId: this._getId,
			getChildren: this._getChildren,
			canBeSelected: this._canBeSelected,
			searchFn: this._searchFn,
			autoOpen: this._autoOpen,
			searchString: this._sesarchString,
		});

		widget.onSelectionChange.register((node) => {
			this.onSelectionChange.trigger(node);
		});

		return widget;
	}
}
